import axios from "axios";
import { message } from "antd";
import { getFirebaseIDToken } from "./firebase/firebase";
import {
  combinedPackingSlipApi,
  getSooCoolLabels,
  multiplePackingSlipsCombinedApi,
  multiplePicklistsApi,
  packingSlipApi,
  picklistApi,
} from "../urls";
import dayjs from "dayjs";
import i18n from "i18next";

/**
 * Generates a packing slip PDF for an order and either downloads it or opens it for printing
 * Also generates a CMR document for Belgian customers
 * @param {Object} order - The order data
 * @param {Array} customers - Array of customer objects
 * @param {string} [method='download'] - The method to handle the PDF ('download' or 'print')
 * @returns {Promise<void>}
 */
export const generatePackingSlip = async (order, customers, method = "download") => {
  if (!order) {
    message.error(i18n.t("notifications.no_order_data"));
    return;
  }

  try {
    const token = await getFirebaseIDToken();

    // Check if customer is from Belgium
    const customer = customers.find((c) => c.id === order.farmorderCustomerId);
    const isBelgianCustomer = customer?.country?.toLowerCase() === "be";

    // Use appropriate endpoint
    const endpoint = isBelgianCustomer ? combinedPackingSlipApi : packingSlipApi;

    const response = await axios.post(
      endpoint,
      { orderId: order.id },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        responseType: "blob",
      },
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);

    if (method === "download") {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${isBelgianCustomer ? "documents" : "packing-slip"}-${order.farmorderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (method === "print") {
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      } else {
        message.warning(i18n.t("notifications.please_allow_pop_ups"));
      }
    }

    URL.revokeObjectURL(blobUrl);

    const messageKey = isBelgianCustomer ? "documents_success" : "packing_slip_success";
    message.success(
      i18n.t(`notifications.${messageKey}`, {
        method: i18n.t(`notifications.${method === "download" ? "downloaded" : "opened_for_printing"}`),
      }),
    );
  } catch (error) {
    console.error("Error generating documents:", error);
    message.error(i18n.t("notifications.error_generating_documents"));
  }
};

/**
 * Generates packing slips for multiple orders
 * @param {Array} orders - Array of order objects
 * @param {Array} customers - Array of customer objects
 * @param {string} [method='download'] - The method to handle the PDFs ('download' or 'print')
 * @returns {Promise<void>}
 */
export const generateMultiplePackingSlips = async (orders, customers, method = "download") => {
  if (!orders || orders.length === 0) {
    message.error(i18n.t("notifications.no_orders_selected"));
    return;
  }

  try {
    message.loading(i18n.t("notifications.generating_documents"), 0);

    const token = await getFirebaseIDToken();
    const orderIds = orders.map((order) => order.id);

    const response = await axios.post(
      multiplePackingSlipsCombinedApi,
      { orderIds },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        responseType: "blob",
      },
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);

    if (method === "download") {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `order-documents-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (method === "print") {
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      } else {
        message.warning(i18n.t("notifications.please_allow_pop_ups"));
      }
    }

    URL.revokeObjectURL(blobUrl);
    message.destroy();
    message.success(
      i18n.t("notifications.multiple_documents_success", {
        count: orders.length,
        plural: orders.length === 1 ? "" : "s",
        method: i18n.t(`notifications.${method === "download" ? "downloaded" : "opened_for_printing"}`),
      }),
    );
  } catch (error) {
    message.destroy();
    console.error("Error generating documents:", error);
    message.error(i18n.t("notifications.error_generating_documents"));
  }
};

/**
 * Downloads SooCool shipping labels for given order IDs
 * @param {Array} sooCoolIds - Array of SooCool order IDs
 * @param {Function} getFirebaseIDToken - Function to get Firebase auth token
 * @param {Object} message - Antd message object for notifications
 * @returns {Promise<void>}
 */
export const printSooCoolLabelsById = async (sooCoolIds, getFirebaseIDToken, message) => {
  if (sooCoolIds.length === 0) {
    return;
  }

  const token = await getFirebaseIDToken();
  const res = await axios
    .get(getSooCoolLabels + `?orderIds=${sooCoolIds.join(",")}`, {
      headers: { Authorization: token },
      responseType: "blob",
    })
    .catch((err) => {
      console.log(err);
      message.error(err?.response?.data?.message);
    });

  if (res?.data) {
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `SooCool-labels-${dayjs().format("DD-MM-YYYY-HH:mm")}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
};

/**
 * Processes orders for printing, splitting them by delivery type
 * @param {Array} orders - Array of orders to process
 * @param {Array} customers - Array of customer objects
 * @param {Array} products - Array of product objects
 * @param {Array} sooCoolDeliveryLabels - Array of SooCool delivery label codes
 * @returns {Object} - Object containing separated order arrays
 */
export const processOrdersForPrinting = (orders, customers, products, sooCoolDeliveryLabels) => {
  const defaultShippingOrders = [];
  const sooCoolOrders = [];
  const pickListOrders = [];

  orders.forEach((order) => {
    const customer = customers.find((customer) => customer.id === order.farmorderCustomerId);
    const effectiveDeliveryLabel = order?.deliveryLabel || customer?.deliveryLabel;

    if (sooCoolDeliveryLabels.includes(effectiveDeliveryLabel)) {
      if (order.sooCoolId) {
        sooCoolOrders.push(order.sooCoolId);

        // Use crates value from order (null = 1, otherwise the actual value)
        const totalKisten = order.crates || 1;

        pickListOrders.push({
          ...order,
          totalKisten,
        });
      }
    } else {
      defaultShippingOrders.push({
        ...order,
        customer,
      });
    }
  });

  return {
    defaultShippingOrders,
    sooCoolOrders,
    pickListOrders,
  };
};

/**
 * Generates a picklist PDF for an order and either downloads it or opens it for printing
 * @param {Object} order - The order data
 * @param {Array} customers - Array of customer objects
 * @param {string} [method='download'] - The method to handle the PDF ('download' or 'print')
 * @returns {Promise<void>}
 */
export const generatePicklist = async (order, customers, method = "download") => {
  if (!order) {
    message.error(i18n.t("notifications.no_order_data"));
    return;
  }

  try {
    const token = await getFirebaseIDToken();

    const response = await axios.post(
      picklistApi,
      { orderId: order.id },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        responseType: "blob",
      },
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);

    if (method === "download") {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `picklist-${order.farmorderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (method === "print") {
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      } else {
        message.warning(i18n.t("notifications.please_allow_pop_ups"));
      }
    }

    URL.revokeObjectURL(blobUrl);
    message.success(
      i18n.t("notifications.picking_slip_success", {
        method: i18n.t(`notifications.${method === "download" ? "downloaded" : "opened_for_printing"}`),
      }),
    );
  } catch (error) {
    console.error("Error generating picklist:", error);
    message.error(i18n.t("notifications.error_generating_document"));
  }
};

/**
 * Generates picklists for multiple orders
 * @param {Array} orders - Array of order objects
 * @param {Array} customers - Array of customer objects
 * @param {string} [method='download'] - The method to handle the PDFs ('download' or 'print')
 * @returns {Promise<void>}
 */
export const generateMultiplePicklists = async (orders, customers, method = "download") => {
  if (!orders || orders.length === 0) {
    message.error(i18n.t("notifications.no_orders_selected"));
    return;
  }

  try {
    message.loading(i18n.t("notifications.generating_documents"), 0);

    const token = await getFirebaseIDToken();
    const orderIds = orders.map((order) => order.id);

    const response = await axios.post(
      multiplePicklistsApi,
      { orderIds },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        responseType: "blob",
      },
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);

    if (method === "download") {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `picklists-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (method === "print") {
      const printWindow = window.open(blobUrl, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          printWindow.print();
        });
      } else {
        message.warning(i18n.t("notifications.please_allow_pop_ups"));
      }
    }

    URL.revokeObjectURL(blobUrl);
    message.destroy();
    message.success(
      i18n.t("notifications.multiple_documents_success", {
        count: orders.length,
        method: i18n.t(`notifications.${method === "download" ? "downloaded" : "opened_for_printing"}`),
      }),
    );
  } catch (error) {
    message.destroy();
    console.error("Error generating picklists:", error);
    message.error(i18n.t("notifications.error_generating_documents"));
  }
};

/**
 * Generate a printable crates overview for selected orders, grouped by restaurant and delivery label.
 * - Uses provided orders (already filtered)
 * - Respects companyFilter by looking up customer.company
 * - Groups by restaurant name + effective delivery label
 * - Sums crates per group (fallback 1 when missing)
 * - Opens a print window with an English summary table
 *
 * @param {Object} params
 * @param {Array} params.orders - Selected orders
 * @param {Array} params.customers - All customers
 * @param {string} params.companyFilter - Selected company filter (or "All")
 */
export function generateCratesOverview({ orders, customers, companyFilter }) {
  if (!orders || orders.length === 0) {
    return { success: false, reason: "no_orders" };
  }

  // Group by restaurant + effective deliveryLabel
  const groupsMap = new Map();
  orders.forEach((o) => {
    const customer = customers?.find((c) => c.id === o.farmorderCustomerId);
    const restaurantName = o.farmorderCustomerName || customer?.name || "Unknown";
    const effectiveLabel = o.deliveryLabel || customer?.deliveryLabel || "";
    const key = `${restaurantName}__${effectiveLabel}`;
    const crates = typeof o.crates === "number" ? o.crates : 1;

    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        restaurant: restaurantName,
        label: effectiveLabel,
        crates: 0,
      });
    }
    const entry = groupsMap.get(key);
    entry.crates += crates;
  });

  const groups = Array.from(groupsMap.values()).sort((a, b) => {
    if (a.restaurant === b.restaurant) return (a.label || "").localeCompare(b.label || "");
    return a.restaurant.localeCompare(b.restaurant);
  });

  const totalCrates = groups.reduce((sum, g) => sum + (g.crates || 0), 0);

  const title = `Crates overview${companyFilter === "All" ? "" : ` (${companyFilter})`}`;

  const rowsHtml = groups
    .map((g) =>
      `
        <tr>
          <td style="border-right: 1px solid #ddd; border-bottom: 1px solid #ddd; max-width: 20px;"></td>
          <td>${g.restaurant}</td>
          <td>${g.label || "-"}</td>
          <td style="text-align:right; padding-right: 30px;">${g.crates}</td>
        </tr>
      `.trim(),
    )
    .join("");

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; }
          h1 { font-size: 18px; margin: 0 0 16px 0; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border-bottom: 1px solid #ddd; padding: 8px; font-size: 12px; }
          th { text-align: left; background: #f7f7f7; }
          tfoot td { font-weight: bold; border-top: 2px solid #000; }
          .meta { margin-bottom: 4px; color: #666; font-size: 11px; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="meta">Generated on ${dayjs().format("DD-MM-YYYY HH:mm")}</div>
        <div class="meta">Total crates: ${totalCrates}</div>
        <br/>
        <table>
          <thead>
            <tr>
              <th style="max-width: 20px;">Counted</th>
              <th>Restaurant</th>
              <th>Delivery label</th>
              <th style="text-align:right; padding-right: 30px;">Crates</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
          <tfoot>
            <tr>
              <td style="border-right: 1px solid #ddd; border-bottom: 1px solid #ddd; max-width: 20px;"></td>
              <td colspan="2">Total crates</td>
              <td style="text-align:right; padding-right: 30px;">${totalCrates}</td>
            </tr>
          </tfoot>
        </table>
        <script>
          window.addEventListener('load', function(){
            window.print();
            setTimeout(() => window.close(), 200);
          });
        </script>
      </body>
    </html>
  `;

  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    return { success: true };
  }

  return { success: false, reason: "popup_blocked" };
}
